import { useState } from 'react'
import { adminApi } from '../../lib/adminApi'
import { TrendGrid } from './Trend'
import {
  DataTable,
  ErrorNote,
  Loading,
  Section,
  Stat,
  fmt,
  useAsync,
} from './parts'

const WINDOWS = [7, 30, 90] as const

export function Overview() {
  const [days, setDays] = useState<number>(30)

  const o = useAsync(() => adminApi.overview(), 'overview')
  const t = useAsync(() => adminApi.timeseries(days), `timeseries:${days}`)
  const merchants = useAsync(() => adminApi.topMerchants(8), 'merchants')
  const categories = useAsync(() => adminApi.topCategories(8), 'categories')

  if (o.error) return <ErrorNote message={o.error} />
  if (!o.data) return <Loading what="metrics" />

  const { users, reminders, engine } = o.data
  const completion =
    reminders['total'] ? Math.round((reminders['completed']! / reminders['total']!) * 100) : 0

  return (
    <>
      <div className="staff-stats">
        <Stat
          label="Accounts"
          value={fmt(users['total'])}
          sub={`+${fmt(users['new_7d'])} this week`}
          tone="teal"
        />
        <Stat
          label="Active this week"
          value={fmt(users['active_7d'])}
          sub={`${fmt(users['active_24h'])} in the last day`}
          tone="blue"
        />
        <Stat
          label="Active reminders"
          value={fmt(reminders['active'])}
          sub={`${fmt(reminders['total'])} ever created`}
        />
        <Stat
          label="Completed"
          value={`${completion}%`}
          sub={`${fmt(reminders['completed_7d'])} closed this week`}
        />
        <Stat
          label="Nudges fired"
          value={fmt(engine['triggers_7d'])}
          sub={`${fmt(engine['triggers_open'])} fences still open`}
          tone="amber"
        />
        <Stat
          label="Push reach"
          value={fmt(engine['push_users'])}
          sub={`${fmt(engine['push_subs'])} devices`}
        />
      </div>

      <div className="staff-section__head staff-section__head--pad">
        <h2 className="staff-hidden">Window</h2>
        <div className="staff-seg" role="group" aria-label="Time window">
          {WINDOWS.map((d) => (
            <button
              key={d}
              aria-pressed={days === d}
              className="staff-seg__btn"
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {t.error && <ErrorNote message={t.error} />}
      {t.data && <TrendGrid points={t.data} />}

      <Section title="Plans">
        <div className="staff-stats staff-stats--tight">
          <Stat label="Free" value={fmt(users['tier_free'])} />
          <Stat label="Pro" value={fmt(users['tier_pro'])} />
          <Stat label="Family" value={fmt(users['tier_family'])} />
          <Stat label="Suspended" value={fmt(users['suspended'])} tone="red" />
          <Stat label="Staff" value={fmt(users['admins'])} />
          <Stat label="Households" value={fmt(engine['households'])} />
        </div>
      </Section>

      <Section title="What people bind reminders to">
        {merchants.error && <ErrorNote message={merchants.error} />}
        <DataTable
          rows={merchants.data ?? []}
          rowId={(r) => r.key}
          empty="No reminders yet."
          columns={[
            { key: 'label', head: 'Merchant class', render: (r) => r.label },
            {
              key: 'class',
              head: 'Binding',
              render: (r) => <code className="staff-code">{r.key}</code>,
            },
            { key: 'r', head: 'Reminders', numeric: true, render: (r) => fmt(r.reminders) },
            { key: 'a', head: 'Active', numeric: true, render: (r) => fmt(r.active) },
            { key: 'u', head: 'Users', numeric: true, render: (r) => fmt(r.users) },
          ]}
        />
      </Section>

      <Section title="Categories in use">
        {categories.error && <ErrorNote message={categories.error} />}
        <DataTable
          rows={categories.data ?? []}
          rowId={(r) => r.key}
          empty="No categories in use yet."
          columns={[
            { key: 'label', head: 'Category', render: (r) => r.label },
            { key: 'slug', head: 'Slug', render: (r) => <code className="staff-code">{r.key}</code> },
            { key: 'r', head: 'Reminders', numeric: true, render: (r) => fmt(r.reminders) },
            { key: 'u', head: 'Users', numeric: true, render: (r) => fmt(r.users) },
          ]}
        />
      </Section>

      <Section title="Store cache">
        <div className="staff-stats staff-stats--tight">
          <Stat label="Cached stores" value={fmt(engine['stores'])} />
          <Stat label="Distinct brands" value={fmt(engine['store_brands'])} />
          <Stat
            label="Stale (7d+)"
            value={fmt(engine['stale_stores'])}
            tone={engine['stale_stores'] ? 'amber' : undefined}
          />
          <Stat label="Cached lookups" value={fmt(engine['cached_queries'])} />
          <Stat label="Positions (24h)" value={fmt(engine['positions_24h'])} />
          <Stat label="Nudges all time" value={fmt(engine['triggers'])} />
        </div>
      </Section>
    </>
  )
}
